// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * The rack's own coordinate system, and the conversions in and out of it: **width** across the rack,
 * **height** up it, **station** along the run. Projected into the frame, spacing is one number per row
 * whatever angle the rack sits at in the model.
 */

import type {
  ProjectedRackPipe,
  RackFrame,
  RackFrameRange,
  RackPipe,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil } from 'helpers/isNotNil';
import { isInsignificant, isSignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';

/** Conduits whose heights are within this distance of each other sit on the same row. */
export const TIER_TOLERANCE_FT = 0.15;

/** A world-space point projected into a {@link RackFrame}. All values in feet. */
type RackFramePoint = {
  /** Position across the rack. */
  widthFt: number;
  /** Position up the rack. */
  heightFt: number;
  /** Position along the conduit run. */
  stationFt: number;
};

/** A unit vector perpendicular to `axis` and as horizontal as the model's up direction allows. */
const derivePerpendicular = (axis: THREE.Vector3): THREE.Vector3 | null => {
  for (const up of [new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0)]) {
    const perpendicular = new THREE.Vector3().crossVectors(axis, up);
    if (isSignificant(perpendicular.lengthSq())) return perpendicular.normalize();
  }
  return null;
};

/**
 * Build the rack frame for a group of parallel conduits, or `null` when no width direction can be derived
 * from their axis. Axes are flipped to agree with the first conduit's before averaging, since start/end
 * order comes from the model and opposite-pointing conduits would otherwise cancel out.
 */
export const buildRackFrame = (pipes: RackPipe[]): RackFrame | null => {
  const [firstPipe] = pipes;
  if (isNil(firstPipe)) return null;

  const referenceAxis = firstPipe.axis;
  const axis = new THREE.Vector3();
  const origin = new THREE.Vector3();
  for (const pipe of pipes) {
    const { axis: pipeAxis } = pipe;
    axis.add(pipeAxis.dot(referenceAxis) < 0 ? pipeAxis.clone().negate() : pipeAxis);
    origin.add(pipe.center);
  }
  // Avoids degenerate geometry
  if (isInsignificant(axis.lengthSq())) return null;
  axis.normalize();
  origin.multiplyScalar(1 / pipes.length);

  const widthDirection = derivePerpendicular(axis);
  if (isNil(widthDirection)) return null;
  const heightDirection = new THREE.Vector3().crossVectors(widthDirection, axis).normalize();

  return {
    origin,
    axis,
    widthDirection,
    heightDirection,
  };
};

/** Project a world-space point into a rack's frame. */
export const projectIntoRackFrame = (point: THREE.Vector3, frame: RackFrame): RackFramePoint => {
  const relative = point.clone().sub(frame.origin);
  return {
    widthFt: relative.dot(frame.widthDirection),
    heightFt: relative.dot(frame.heightDirection),
    stationFt: relative.dot(frame.axis),
  };
};

/** The world-space point at a position in a rack's frame — the inverse of {@link projectIntoRackFrame}. */
export const resolveRackFramePoint = (frame: RackFrame, { widthFt, heightFt, stationFt }: RackFramePoint) =>
  frame.origin
    .clone()
    .add(frame.widthDirection.clone().multiplyScalar(widthFt))
    .add(frame.heightDirection.clone().multiplyScalar(heightFt))
    .add(frame.axis.clone().multiplyScalar(stationFt));

export const projectPipesIntoRackFrame = (pipes: RackPipe[], frame: RackFrame): ProjectedRackPipe[] =>
  pipes.map((pipe) => {
    const center = projectIntoRackFrame(pipe.center, frame);
    return {
      ...pipe,
      widthFt: center.widthFt,
      heightFt: center.heightFt,
      startStationFt: projectIntoRackFrame(pipe.start, frame).stationFt,
      endStationFt: projectIntoRackFrame(pipe.end, frame).stationFt,
    };
  });

/** The span a rack's conduits occupy in one rack-frame direction. */
const getPipeRange = (
  /** The values of one conduit to span; a station needs both of its endpoints */
  pipes: ProjectedRackPipe[],
  pickValues: (pipe: ProjectedRackPipe) => number[],
): RackFrameRange => {
  const values = pipes.flatMap(pickValues);
  return { minFt: Math.min(...values), maxFt: Math.max(...values) };
};

/** The span the conduits occupy along the run, in the rack's frame. */
export const getPipeStationRange = (pipes: ProjectedRackPipe[]): RackFrameRange =>
  getPipeRange(pipes, ({ startStationFt, endStationFt }) => [startStationFt, endStationFt]);
